import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import NotificatieBel from './NotificatieBel';
import sharepointConfig from '../config/sharepointConfig';
import { APP_VERSION, DEPLOY_DATE } from '../config/version';
import { valideerWachtwoord } from '../utils/wachtwoord';

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, realUser, isAdmin, isManager, isFlexkracht, loading, logout,
    menuConfig, beschikbareRollen, effectiefRol, switchRol,
    profielLabel, medewerkers, wijzigWachtwoord,
    viewAsUserId, startViewAs, stopViewAs, isViewingAs } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showWwModal, setShowWwModal] = useState(false);
  const [wwForm, setWwForm] = useState({ huidig: '', nieuw: '', bevestig: '' });
  const [wwMsg, setWwMsg] = useState('');
  const [wwBusy, setWwBusy] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Lokale vlag: direct sluiten na succes, ongeacht Firestore-timing
  const [wwVerplichtKlaar, setWwVerplichtKlaar] = useState(false);

  // Verplicht wachtwoord wijzigen (bij eerste login of na admin-reset)
  const mustChangeNow = !wwVerplichtKlaar && !isViewingAs && currentUser?.MustChangePassword === true;

  // Scroll-to-top detection
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect if already installed as PWA
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // PWA install prompt (Android/Chrome)
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // iOS detection + show banner
  useEffect(() => {
    if (isStandalone || !currentUser) return;
    const dismissed = sessionStorage.getItem('pwa-dismissed');
    if (dismissed) return;

    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|Chrome/.test(ua);

    if (iOS && isSafari) {
      setIsIOS(true);
      setShowInstallBanner(true);
    } else if (deferredPrompt) {
      setShowInstallBanner(true);
    }
  }, [deferredPrompt, currentUser, isStandalone]);

  const handleInstall = async () => {
    if (isIOS) {
      // Can't programmatically install on iOS, just show instructions
      dismissInstall();
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
    setDeferredPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    sessionStorage.setItem('pwa-dismissed', '1');
  };

  // Close mobile menu on navigate
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    // Robust scroll-to-top: immediate + rAF + delayed for slow renders
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      const pc = document.querySelector('.page-content');
      if (pc) pc.scrollTop = 0;
    });
  }, [location.pathname, location.search]);

  // Build nav
  const navItems = [];
  navItems.push({ key: '/', icon: '📊', label: 'Dashboard' });
  if (isAdmin) {
    navItems.push({ key: '/indienen', icon: '📝', label: 'Indienen' });
    navItems.push({ key: '/verlof-overzicht', icon: '📋', label: 'Verlofoverzicht' });
    navItems.push({ key: '/verlof-medewerkers', icon: '👤', label: 'Verlof medewerkers' });
    navItems.push({ key: '/overuren', icon: '⏰', label: 'Overuren' });
    navItems.push({ key: '/flex-planner', icon: '🔀', label: 'Flex Planner' });
    navItems.push({ key: '/kalender', icon: '📅', label: 'Kalender' });
    navItems.push({ key: '/admin', icon: '⚙️', label: 'Beheer' });
    navItems.push({ key: '/outlook-sync', icon: '📅', label: 'Outlook Sync' });
  } else if (isFlexkracht) {
    navItems.push({ key: '/kalender', icon: '📅', label: 'Kalender' });
    navItems.push({ key: '/flex', icon: '⚡', label: 'Mijn Flex' });
  } else if (isManager) {
    navItems.push({ key: '/indienen', icon: '📝', label: 'Indienen' });
    navItems.push({ key: '/verlof-overzicht', icon: '📋', label: 'Overzicht' });
    navItems.push({ key: '/verlof-medewerkers', icon: '👤', label: 'Verlof medewerkers' });
    navItems.push({ key: '/overuren', icon: '⏰', label: 'Overuren' });
    navItems.push({ key: '/flex-planner', icon: '🔀', label: 'Flex Planner' });
    navItems.push({ key: '/kalender', icon: '📅', label: 'Kalender' });
    navItems.push({ key: '/admin', icon: '🔄', label: 'Roosterwijzigingen' });
  } else {
    // Medewerker
    navItems.push({ key: '/indienen', icon: '📝', label: 'Indienen' });
    navItems.push({ key: '/verlof-overzicht', icon: '📋', label: 'Overzicht' });
    navItems.push({ key: '/overuren', icon: '⏰', label: 'Overuren' });
    navItems.push({ key: '/kalender', icon: '📅', label: 'Kalender' });
    if ((currentUser?.WeekFlexUren || 0) > 0) {
      navItems.push({ key: '/flex-planner', icon: '🔀', label: 'Flex Planner' });
    }
    if (currentUser?.HeeftFlexProfiel) {
      navItems.push({ key: '/flex', icon: '⚡', label: 'Mijn Flex' });
    }
  }
  navItems.push({ key: '/handleiding', icon: '📖', label: 'Handleiding' });

  // Apply menu config: custom labels and order
  if (menuConfig && menuConfig.length > 0) {
    const configMap = {};
    menuConfig.forEach(mc => { configMap[mc.key] = mc; });
    navItems.forEach(item => {
      const mc = configMap[item.key];
      if (mc && mc.label) item.label = mc.label;
    });
    // Sort by custom order if defined
    const hasOrder = menuConfig.some(mc => mc.order !== undefined);
    if (hasOrder) {
      const orderMap = {};
      menuConfig.forEach(mc => { if (mc.order !== undefined) orderMap[mc.key] = mc.order; });
      navItems.sort((a, b) => {
        const oa = orderMap[a.key] !== undefined ? orderMap[a.key] : 999;
        const ob = orderMap[b.key] !== undefined ? orderMap[b.key] : 999;
        return oa - ob;
      });
    }
  }

  const rolInfo = sharepointConfig.rollen.find(r => r.key === effectiefRol) || sharepointConfig.rollen[2];
  const heeftMeerdereRollen = beschikbareRollen.length > 1;

  // Mobile: toon eerste 4 items direct, de rest via ≡ hamburger
  const TAB_VISIBLE = 5;
  const tabBarItems = navItems.slice(0, TAB_VISIBLE);
  const moreItems = navItems.slice(TAB_VISIBLE);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
      {/* ====== DESKTOP SIDEBAR ====== */}
      <nav className="desk-sidebar" style={{
        position: 'fixed', top: 0, left: 0, height: '100vh', width: collapsed ? 64 : 240,
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        color: '#fff', display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s ease', zIndex: 100, overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '20px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.1)', gap: 12, minHeight: 64 }}
          onClick={() => setCollapsed(!collapsed)}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>🛏️</span>
          {!collapsed && <span style={{ fontSize: 20, fontWeight: 700, whiteSpace: 'nowrap' }}>SL Verlof</span>}
        </div>
        <div style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
          {navItems.map(item => {
            const isActive = location.pathname === item.key;
            return (
              <div key={item.key} onClick={() => navigate(item.key)}
                style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', gap: 12,
                  color: isActive ? '#E86E1C' : 'rgba(255,255,255,0.7)',
                  background: isActive ? 'rgba(232,110,28,0.2)' : 'transparent',
                }}>
                <span style={{ fontSize: 20, flexShrink: 0, width: 28, textAlign: 'center' }}>{item.icon}</span>
                {!collapsed && <span style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>{item.label}</span>}
              </div>
            );
          })}
        </div>
        {!collapsed && currentUser && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {/* Profiel label */}
            <div style={{ fontSize: 12, fontWeight: 700, color: rolInfo.color || '#E86E1C', marginBottom: 6, letterSpacing: 0.3 }}>
              {rolInfo.icon} {profielLabel}
            </div>
            {isViewingAs && (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
                via {realUser?.Naam}
              </div>
            )}
            {heeftMeerdereRollen && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                {beschikbareRollen.map(r => {
                  const ri = sharepointConfig.rollen.find(x => x.key === r) || {};
                  const isActive = r === effectiefRol && !isViewingAs;
                  return (
                    <button key={r} onClick={() => { stopViewAs(); switchRol(r); navigate('/'); }}
                      style={{ padding: '4px 10px', borderRadius: 6, border: isActive ? `2px solid ${ri.color}` : '1px solid rgba(255,255,255,0.2)',
                        background: isActive ? `${ri.color}30` : 'transparent', color: isActive ? ri.color : 'rgba(255,255,255,0.6)',
                        fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {ri.icon} {ri.text}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Admin: inloggen als medewerker */}
            {realUser?.IsAdmin && (
              <div style={{ marginTop: 6 }}>
                <select
                  value={viewAsUserId || ''}
                  onChange={e => {
                    const id = e.target.value;
                    if (id) { startViewAs(id); navigate('/'); }
                    else { stopViewAs(); }
                  }}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>
                  <option value="" style={{ color: '#000' }}>👁️ Inloggen als medewerker...</option>
                  {(medewerkers || []).filter(m => (m.Status || 'actief') !== 'uitdienst' && m.Id !== realUser?.id).sort((a,b) => a.Naam.localeCompare(b.Naam)).map(m => (
                    <option key={m.Id} value={m.Id} style={{ color: '#000' }}>{m.Naam} ({m.Rol})</option>
                  ))}
                </select>
                {isViewingAs && (
                  <button onClick={() => { stopViewAs(); switchRol('admin'); navigate('/'); }}
                    style={{ marginTop: 4, width: '100%', padding: '4px 8px', borderRadius: 6, border: 'none',
                      background: '#D13438', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    ✕ Terug naar eigen profiel
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div onClick={() => { setShowWwModal(true); setWwForm({ huidig: '', nieuw: '', bevestig: '' }); setWwMsg(''); }} 
            style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', cursor: 'pointer', gap: 12, color: 'rgba(255,255,255,0.5)' }}>
            <span style={{ fontSize: 20, flexShrink: 0, width: 28, textAlign: 'center' }}>🔑</span>
            {!collapsed && <span style={{ fontSize: 13, fontWeight: 500 }}>Wachtwoord wijzigen</span>}
          </div>
          <div onClick={logout} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', cursor: 'pointer', gap: 12, color: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: 20, flexShrink: 0, width: 28, textAlign: 'center' }}>🚪</span>
            {!collapsed && <span style={{ fontSize: 14, fontWeight: 500 }}>Uitloggen</span>}
          </div>
        </div>
      </nav>

      {/* ====== MOBILE HEADER ====== */}
      <header className="mob-header" style={{
        display: 'none', position: 'sticky', top: 0, zIndex: 90,
        background: '#1a1a2e', color: '#fff',
        paddingTop: 'max(6px, env(safe-area-inset-top))',
      }}>
        {/* Top row: logo + role + actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🛏️</span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>SL Verlof</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {heeftMeerdereRollen && !isViewingAs ? (
              <select value={effectiefRol} onChange={e => { switchRol(e.target.value); navigate('/'); }}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 6px', fontSize: 10, cursor: 'pointer' }}>
                {beschikbareRollen.map(r => {
                  const ri = sharepointConfig.rollen.find(x => x.key === r) || {};
                  return <option key={r} value={r} style={{ color: '#000' }}>{ri.icon} {ri.text}</option>;
                })}
              </select>
            ) : (
              <span style={{ fontSize: 10, color: isViewingAs ? '#FFB900' : 'rgba(255,255,255,0.6)', fontWeight: isViewingAs ? 700 : 400 }}>
                {rolInfo.icon} {currentUser?.Naam?.split(' ')[0]}
                {isViewingAs && <span style={{ fontSize: 9 }}> (via {realUser?.Naam?.split(' ')[0]})</span>}
              </span>
            )}
            {isViewingAs && (
              <button onClick={() => { stopViewAs(); switchRol('admin'); navigate('/'); }}
                style={{ background: '#D13438', border: 'none', color: '#fff', borderRadius: 6, padding: '3px 6px', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}>✕</button>
            )}
            <NotificatieBel />
            <button onClick={logout} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>Uit</button>
          </div>
        </div>
        {/* Bottom row: back/forward + page title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px 8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => window.history.back()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 22, padding: '6px 14px', cursor: 'pointer', lineHeight: 1, borderRadius: 8, minWidth: 44, minHeight: 36 }}>‹</button>
          <button onClick={() => window.history.forward()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 22, padding: '6px 14px', cursor: 'pointer', lineHeight: 1, borderRadius: 8, minWidth: 44, minHeight: 36 }}>›</button>
          <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 4, color: '#fff' }}>
            {navItems.find(i => i.key === location.pathname)?.icon} {navItems.find(i => i.key === location.pathname)?.label || 'Verlofbeheer'}
          </span>
        </div>
      </header>

      {/* ====== MAIN CONTENT ====== */}
      <main style={{
        marginLeft: 'var(--sidebar-w, 0px)',
        flex: 1, background: '#f5f5f5',
      }}>
        <style>{`
          @media (min-width: 769px) { main { --sidebar-w: ${collapsed ? 64 : 240}px; } }
          @media (max-width: 768px) { main { --sidebar-w: 0px !important; } }
        `}</style>
        {/* ====== MOBILE SCROLL WRAPPER ======
            overflow-x: hidden op een inner div werkt WEL op iOS (i.t.t. html/body).
            Dit blok vangt alle ongewenste horizontale scroll op pagina-niveau.
            Scroll-containers (.r-cal-scroll, .r-table-wrap) binnenin hebben
            overflow-x: scroll en creëren hun eigen scroll-context. */}
        <div className="mob-overflow-guard">
        {/* Desktop top bar */}
        <header className="desk-topbar" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 32px', background: '#fff', borderBottom: '1px solid #edebe9',
          position: 'sticky', top: 0, zIndex: 50,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {location.pathname !== '/' && (
              <>
                <span onClick={() => navigate('/')} style={{ fontSize: 14, color: '#605e5c', cursor: 'pointer', fontWeight: 500 }}>
                  📊 Dashboard
                </span>
                <span style={{ fontSize: 14, color: '#c8c6c4' }}>›</span>
              </>
            )}
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
              {navItems.find(i => i.key === location.pathname)?.icon} {navItems.find(i => i.key === location.pathname)?.label || 'Verlofbeheer'}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <NotificatieBel />
            <span style={{ fontSize: 12, fontWeight: 600, color: rolInfo.color || '#605e5c', background: `${rolInfo.color || '#605e5c'}15`, padding: '4px 12px', borderRadius: 20 }}>
              {rolInfo.icon} {profielLabel}
            </span>
            <span className="date-display" style={{ fontSize: 13, color: '#605e5c' }}>
              {new Date().toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </header>
        <div className="page-content" style={{ padding: 32, animation: 'fadeIn 0.3s ease' }}>
          {/* ViewAs banner */}
          {isViewingAs && (
            <div style={{ background: '#FFF4CE', border: '2px solid #FFB900', borderRadius: 10, padding: '10px 16px',
              marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#7A6400' }}>
                👁️ {profielLabel} <span style={{ fontWeight: 400 }}>(bekeken via {realUser?.Naam})</span>
              </div>
              <button onClick={() => { stopViewAs(); switchRol('admin'); navigate('/'); }}
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#D13438', color: '#fff',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                ✕ Terug
              </button>
            </div>
          )}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#605e5c' }}>
              <div style={{ width: 40, height: 40, border: '4px solid #f3f3f3', borderTop: '4px solid #E86E1C', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 16 }} />
              <p>Gegevens laden...</p>
            </div>
          ) : children}
        </div>
        </div>{/* end mob-overflow-guard */}
      </main>

      {/* ====== MOBILE TAB BAR ====== */}
      <nav className="mob-tabbar" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid #edebe9',
        justifyContent: 'space-around', alignItems: 'flex-start',
        zIndex: 100,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
        height: 'calc(64px + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {tabBarItems.map(item => {
          const isActive = location.pathname === item.key;
          return (
            <button key={item.key} onClick={() => navigate(item.key)}
              title={item.label}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '0 2px', border: 'none', background: 'transparent',
                color: isActive ? '#E86E1C' : '#605e5c', cursor: 'pointer',
                borderTop: isActive ? '3px solid #E86E1C' : '3px solid transparent',
                minWidth: 0, height: 64, boxSizing: 'border-box',
              }}>
              <span style={{ fontSize: 24, lineHeight: 1, display: 'block' }}>{item.icon}</span>
            </button>
          );
        })}

        {/* ≡ Hamburger voor extra items */}
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          title="Meer opties"
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '0 2px', border: 'none', background: 'transparent',
            color: mobileMenuOpen ? '#E86E1C' : isViewingAs ? '#D13438' : '#605e5c',
            cursor: 'pointer',
            borderTop: mobileMenuOpen ? '3px solid #E86E1C' : '3px solid transparent',
            minWidth: 0, height: 64, boxSizing: 'border-box', position: 'relative',
          }}>
          {/* 3 streepjes hamburger icoon */}
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
            <span style={{ width: 20, height: 2.5, background: 'currentColor', borderRadius: 2, display: 'block' }} />
            <span style={{ width: 20, height: 2.5, background: 'currentColor', borderRadius: 2, display: 'block' }} />
            <span style={{ width: 20, height: 2.5, background: 'currentColor', borderRadius: 2, display: 'block' }} />
          </span>
          {isViewingAs && (
            <span style={{
              position: 'absolute', top: 10, right: '20%',
              width: 7, height: 7, borderRadius: '50%',
              background: '#D13438', border: '1.5px solid #fff',
            }} />
          )}
        </button>
      </nav>

      {/* ====== MOBILE PROFIEL DRAWER (slide-up) ====== */}
      {mobileMenuOpen && (
        <>
          <div onClick={() => setMobileMenuOpen(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
          }} />
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 201,
            background: '#fff', borderRadius: '20px 20px 0 0',
            padding: '12px 16px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
            animation: 'slideUp 0.2s ease',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ width: 40, height: 4, background: '#d8d6d4', borderRadius: 2, margin: '0 auto 12px' }} />

            {/* Extra navigatie-items (overflow van de balk) */}
            {moreItems.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {moreItems.map(item => {
                  const isActive = location.pathname === item.key;
                  return (
                    <button key={item.key} onClick={() => { navigate(item.key); setMobileMenuOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
                        borderRadius: 10, border: isActive ? '2px solid #E86E1C' : '1px solid #edebe9',
                        background: isActive ? '#FFF5F0' : '#faf9f8',
                        color: isActive ? '#E86E1C' : '#323130',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}>
                      <span style={{ fontSize: 18 }}>{item.icon}</span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Scheidslijn */}
            <div style={{ borderTop: '1px solid #edebe9', margin: '0 -4px 12px' }} />

            {/* Profiel info */}
            <div style={{ padding: '4px 0 10px', marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: rolInfo.color || '#E86E1C' }}>
                {rolInfo.icon} {profielLabel}
              </div>
              {isViewingAs && (
                <div style={{ fontSize: 12, color: '#a19f9d', marginTop: 2 }}>via {realUser?.Naam}</div>
              )}
            </div>

            {heeftMeerdereRollen && !isViewingAs && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {beschikbareRollen.map(r => {
                  const ri = sharepointConfig.rollen.find(x => x.key === r) || {};
                  const isRolActive = r === effectiefRol;
                  return (
                    <button key={r} onClick={() => { switchRol(r); setMobileMenuOpen(false); navigate('/'); }}
                      style={{ padding: '5px 12px', borderRadius: 8, border: isRolActive ? `2px solid ${ri.color}` : '1px solid #edebe9',
                        background: isRolActive ? `${ri.color}15` : '#faf9f8', color: isRolActive ? ri.color : '#605e5c',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {ri.icon} {ri.text}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Admin: inloggen als medewerker */}
            {realUser?.IsAdmin && (
              <div style={{ marginBottom: 12 }}>
                <select
                  value={viewAsUserId || ''}
                  onChange={e => {
                    const id = e.target.value;
                    if (id) { startViewAs(id); setMobileMenuOpen(false); navigate('/'); }
                    else { stopViewAs(); }
                  }}
                  style={{ width: '100%', border: '1px solid #edebe9', borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: '#323130' }}>
                  <option value="">👁️ Inloggen als medewerker...</option>
                  {(medewerkers || []).filter(m => (m.Status || 'actief') !== 'uitdienst' && m.Id !== realUser?.id).sort((a,b) => a.Naam.localeCompare(b.Naam)).map(m => (
                    <option key={m.Id} value={m.Id}>{m.Naam}</option>
                  ))}
                </select>
                {isViewingAs && (
                  <button onClick={() => { stopViewAs(); switchRol('admin'); setMobileMenuOpen(false); navigate('/'); }}
                    style={{ marginTop: 6, width: '100%', padding: '6px 10px', borderRadius: 8, border: 'none',
                      background: '#D13438', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    ✕ Terug naar eigen profiel
                  </button>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => { setMobileMenuOpen(false); setShowWwModal(true); setWwForm({ huidig: '', nieuw: '', bevestig: '' }); setWwMsg(''); }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #edebe9', background: '#faf9f8', color: '#323130', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                🔑 Wachtwoord
              </button>
              <button onClick={() => { setMobileMenuOpen(false); logout(); }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#D13438', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                🚪 Uitloggen
              </button>
            </div>
          </div>
        </>
      )}

      {/* ====== PWA INSTALL BANNER ====== */}
      {showInstallBanner && (
        <div className="pwa-banner" style={{
          position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 300,
          background: '#1a1a2e', color: '#fff', borderRadius: 16,
          padding: '16px 20px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isIOS ? 10 : 0 }}>
            <span style={{ fontSize: 28, flexShrink: 0 }}>🛏️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Installeer de app!</div>
              {isIOS ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.5 }}>
                  Tik op <span style={{ fontSize: 16, verticalAlign: 'middle' }}>⎙</span> (deel-knop) en kies <strong>"Zet op beginscherm"</strong>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>Voeg SL Verlof toe aan je thuisscherm.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button onClick={dismissInstall} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Later</button>
              {!isIOS && <button onClick={handleInstall} style={{ background: '#E86E1C', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Installeren</button>}
            </div>
          </div>
        </div>
      )}

      {/* ====== WACHTWOORD WIJZIGEN MODAL ====== */}
      {/* ====== VERPLICHT WACHTWOORD WIJZIGEN MODAL (niet sluitbaar) ====== */}
      {/* ====== VERPLICHT WACHTWOORD WIJZIGEN MODAL (niet sluitbaar) ====== */}
      {mustChangeNow && (() => {
        // Eigen state via closure — deelt NIET de wwForm van de vrijwillige modal
        const forceSubmit = async () => {
          const nieuw = document.getElementById('force-ww-nieuw')?.value || '';
          const bevestig = document.getElementById('force-ww-bevestig')?.value || '';
          const msg = document.getElementById('force-ww-msg');
          const btn = document.getElementById('force-ww-btn');
          const check = valideerWachtwoord(nieuw);
          if (!check.valid) { if (msg) msg.textContent = '❌ ' + check.errors.join(', '); return; }
          if (nieuw !== bevestig) { if (msg) msg.textContent = '❌ Wachtwoorden komen niet overeen'; return; }
          if (btn) btn.disabled = true;
          if (msg) msg.textContent = '⏳ Bezig...';
          try {
            await wijzigWachtwoord(currentUser?.id, null, nieuw);
            setWwVerplichtKlaar(true);
          } catch (err) {
            if (msg) msg.textContent = '❌ ' + err.message;
            if (btn) btn.disabled = false;
          }
        };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,52,96,0.92)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 420, width: '100%',
              boxShadow: '0 12px 48px rgba(0,0,0,0.4)' }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 40 }}>🔐</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '10px 0 6px' }}>Kies een nieuw wachtwoord</h2>
                <p style={{ fontSize: 13, color: '#605e5c', margin: 0, lineHeight: 1.5 }}>
                  Je beheerder heeft een tijdelijk wachtwoord ingesteld.<br/>
                  Kies nu een eigen wachtwoord om door te gaan.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Nieuw wachtwoord</label>
                  <input id="force-ww-nieuw" type="password"
                    style={{ width: '100%', padding: '11px 14px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    placeholder="Min. 8 tekens, hoofdletter, cijfer" autoComplete="new-password" autoFocus
                    onKeyDown={e => e.key === 'Enter' && forceSubmit()} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Bevestig nieuw wachtwoord</label>
                  <input id="force-ww-bevestig" type="password"
                    style={{ width: '100%', padding: '11px 14px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    placeholder="Herhaal wachtwoord" autoComplete="new-password"
                    onKeyDown={e => e.key === 'Enter' && forceSubmit()} />
                </div>
              </div>
              <div id="force-ww-msg" style={{ marginTop: 10, fontSize: 13, color: '#D83B01', minHeight: 20 }}></div>
              <button id="force-ww-btn" onClick={forceSubmit}
                style={{ marginTop: 12, width: '100%', padding: 14, border: 'none', borderRadius: 10,
                  background: '#E86E1C', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                ✓ Wachtwoord instellen en doorgaan
              </button>
              <button onClick={logout} style={{ marginTop: 8, width: '100%', padding: '10px', border: 'none',
                borderRadius: 8, background: 'transparent', color: '#a19f9d', fontSize: 13, cursor: 'pointer' }}>
                Uitloggen
              </button>
            </div>
          </div>
        );
      })()}

      {/* ====== WACHTWOORD WIJZIGEN MODAL (vrijwillig) ====== */}
      {showWwModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => !wwBusy && setShowWwModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#1a1a2e' }}>🔑 Wachtwoord wijzigen</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Huidig wachtwoord</label>
                <input type="password" value={wwForm.huidig} onChange={e => setWwForm({ ...wwForm, huidig: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Voer je huidige wachtwoord in" autoComplete="current-password" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Nieuw wachtwoord</label>
                <input type="password" value={wwForm.nieuw} onChange={e => setWwForm({ ...wwForm, nieuw: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Min. 8 tekens, hoofdletter, kleine letter, cijfer" autoComplete="new-password" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Bevestig nieuw wachtwoord</label>
                <input type="password" value={wwForm.bevestig} onChange={e => setWwForm({ ...wwForm, bevestig: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Herhaal nieuw wachtwoord" autoComplete="new-password" />
              </div>
            </div>
            {wwForm.nieuw && !valideerWachtwoord(wwForm.nieuw).valid && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#D83B01' }}>
                {valideerWachtwoord(wwForm.nieuw).errors.map((e, i) => <div key={i}>• {e}</div>)}
              </div>
            )}
            {wwMsg && (
              <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: wwMsg.startsWith('✅') ? '#dff6dd' : '#fde7e9',
                color: wwMsg.startsWith('✅') ? '#107C10' : '#D13438' }}>
                {wwMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowWwModal(false)} disabled={wwBusy}
                style={{ flex: 1, padding: '10px', border: '1px solid #edebe9', borderRadius: 8, background: '#faf9f8', color: '#323130', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Annuleren
              </button>
              <button disabled={wwBusy || !wwForm.huidig || !wwForm.nieuw || !wwForm.bevestig}
                onClick={async () => {
                  const wwCheck = valideerWachtwoord(wwForm.nieuw);
                  if (!wwCheck.valid) { setWwMsg('❌ ' + wwCheck.errors.join(', ')); return; }
                  if (wwForm.nieuw !== wwForm.bevestig) { setWwMsg('❌ Wachtwoorden komen niet overeen'); return; }
                  setWwBusy(true);
                  try {
                    await wijzigWachtwoord(currentUser?.id, wwForm.huidig, wwForm.nieuw);
                    setWwMsg('✅ Wachtwoord succesvol gewijzigd!');
                    setTimeout(() => setShowWwModal(false), 1500);
                  } catch (err) {
                    setWwMsg(`❌ ${err.message}`);
                  }
                  setWwBusy(false);
                }}
                style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 8, background: '#E86E1C', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  opacity: (!wwForm.huidig || !wwForm.nieuw || !wwForm.bevestig) ? 0.4 : 1 }}>
                {wwBusy ? '⏳ Bezig...' : '✓ Wijzigen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to top button */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="scroll-top-btn"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 99,
            width: 44, height: 44, borderRadius: '50%',
            background: '#1a1a2e', color: '#fff', border: '2px solid rgba(255,255,255,0.2)',
            fontSize: 18, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Naar boven">
          ↑
        </button>
      )}

      {/* Footer */}
      <footer className="app-footer" style={{
        textAlign: 'center', padding: '16px 12px',
        borderTop: '1px solid #edebe9', color: '#a19f9d', fontSize: 11,
        lineHeight: 1.6, background: '#f5f5f5',
      }}>
        <style>{`
          @media (min-width: 769px) { .app-footer { margin-left: ${collapsed ? 64 : 240}px; } }
          @media (max-width: 768px) { .app-footer { padding-bottom: 80px; } }
        `}</style>
        <div>Copyright © {new Date().getFullYear()} Willem Reichardt — Slaaploods.nl B.V.</div>
        <div style={{ fontSize: 10, color: '#c8c6c4' }}>v{APP_VERSION} · Deploy {DEPLOY_DATE}</div>
      </footer>
    </div>
  );
}

export default Layout;
